import {
  acquireDatabaseOfflineCopy,
  type DatabaseOfflineCopyPort,
} from '@blocksuite/affine-block-database';
import { Service } from '@toeverything/infra';

import { createDatabaseOfflineCopyPort } from '../../../blocksuite/view-extensions/editor-config/database-offline-copy';
import type { WorkspaceService } from '../../workspace';
import {
  type AppSettingsWorkspace,
  databaseRootManifest,
  watchDatabaseRoot,
} from '../stores/app-settings-databases';
import {
  type AppSettingsLocation,
  appSettingsLocationTables,
} from '../stores/view-config-row.store';

/**
 * Keeps every Config table of each opened App Container in this device's
 * storage for the rest of the workspace session, whether a block shows it
 * or not: the settings open and change offline. The copies are silent;
 * the Config tables are no database the user turned offline.
 */
export class AppSettingsOfflineCopyService extends Service {
  private readonly releases = new Map<string, () => void>();
  private port: DatabaseOfflineCopyPort | null = null;

  constructor(private readonly workspaceService: WorkspaceService) {
    super();
  }

  keep(location: AppSettingsLocation): void {
    const workspace = this.workspaceService.workspace
      .docCollection as unknown as AppSettingsWorkspace;
    const port = this.silentPort();
    for (const { blockId } of appSettingsLocationTables(location)) {
      if (this.releases.has(blockId)) continue;
      const { copy, release } = acquireDatabaseOfflineCopy(port, {
        databaseId: blockId,
        manifest: () => databaseRootManifest(workspace, blockId),
        subscribe: onChange => watchDatabaseRoot(workspace, blockId, onChange),
      });
      copy.setEnabled(true);
      this.releases.set(blockId, release);
    }
  }

  override dispose(): void {
    for (const release of this.releases.values()) release();
    this.releases.clear();
    super.dispose();
  }

  private silentPort(): DatabaseOfflineCopyPort {
    if (this.port) return this.port;
    const shared = createDatabaseOfflineCopyPort(this.framework);
    if (!shared)
      throw new Error('AppSettingsOfflineCopyService: no workspace storage');
    this.port = { ...shared, notify: () => {} };
    return this.port;
  }
}
